import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, History } from "lucide-react";
import type { useWebSocket } from "@/hooks/useWebSocket";
import { ProgressBar } from "@/components/ProgressBar";
import { AccuracyTrend } from "@/components/AccuracyTrend";
import { ScoreDistribution } from "@/components/ScoreDistribution";
import { SuccessFailurePie } from "@/components/SuccessFailurePie";
import { TaskList } from "@/components/TaskList";
import { RunHistory } from "@/components/RunHistory";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState } from "@/components/ui";

type DashboardState = ReturnType<typeof useWebSocket>;

function agentName(path?: string) {
  if (!path) return "–";
  const parts = path.split("/");
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0] || "–";
}

function Meta({ label, value, title }: { label: string; value: ReactNode; title?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate font-mono text-sm" title={title}>
        {value || "–"}
      </div>
    </div>
  );
}

function ChartCard({ children }: { children: ReactNode }) {
  return (
    <Card className="h-96 overflow-hidden">
      <CardBody className="h-full">{children}</CardBody>
    </Card>
  );
}

const LEGEND = [
  { label: "In wachtrij", cls: "bg-sl-grey-300" },
  { label: "Bezig", cls: "bg-sl-yellow" },
  { label: "Voltooid", cls: "bg-success" },
  { label: "Mislukt", cls: "bg-destructive" },
];

export default function RunsPage({
  state,
  onSelectRun,
  onBackToLive,
}: {
  state: DashboardState;
  onSelectRun: (runId: string) => void;
  onBackToLive: () => void;
}) {
  const hasRun = Boolean(state.run_id);

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <aside className="max-h-72 shrink-0 overflow-y-auto border-b border-border bg-card lg:max-h-none lg:w-80 lg:border-b-0 lg:border-r">
        <div className="p-4">
          <RunHistory
            onSelectRun={onSelectRun}
            liveRunId={state.liveRunId}
            liveRunInfo={
              state.viewMode === "live"
                ? {
                    run_id: state.run_id || "",
                    agent_path: state.agent_path,
                    model: state.model,
                    dataset_version: state.dataset_version,
                    total_questions: state.total_questions,
                    mean_accuracy: state.metrics?.mean_accuracy,
                  }
                : undefined
            }
            currentRunId={state.run_id}
            viewMode={state.viewMode}
            onBackToLive={onBackToLive}
          />
        </div>
      </aside>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
          {!hasRun ? (
            <Card>
              <EmptyState
                icon={<History className="h-5 w-5" />}
                title="Geen run geselecteerd"
                description="Kies een run in de lijst, of start een nieuwe evaluatie om de voortgang hier live te volgen."
                action={
                  <Link to="/evaluaties">
                    <Button variant="brand">
                      <FlaskConical className="h-4 w-4" />
                      Nieuwe evaluatie
                    </Button>
                  </Link>
                }
              />
            </Card>
          ) : (
            <div className="flex flex-col gap-6 pb-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold tracking-tight">Run</h1>
                  {state.viewMode === "live" ? <Badge tone="danger">● Live</Badge> : <Badge>Historisch</Badge>}
                </div>
                {state.viewMode !== "live" && state.liveRunId && (
                  <Button variant="outline" size="sm" onClick={onBackToLive}>
                    Terug naar live run
                  </Button>
                )}
              </div>

              <Card>
                <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                  <Meta label="Agent" value={agentName(state.agent_path)} title={state.agent_path} />
                  <Meta label="Model" value={state.model} title={state.model} />
                  <Meta label="Vragenlijst" value={state.dataset_version} />
                  <Meta label="Workers" value={state.parallel_workers} />
                  <Meta label="Run-ID" value={state.run_id} title={state.run_id} />
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <ProgressBar state={state} />
                </CardBody>
              </Card>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <ChartCard>
                  <AccuracyTrend metrics={state.metrics} tasks={state.tasks} totalQuestions={state.total_questions} />
                </ChartCard>
                <ChartCard>
                  <ScoreDistribution metrics={state.metrics} tasks={state.tasks} />
                </ChartCard>
                <ChartCard>
                  <SuccessFailurePie metrics={state.metrics} tasks={state.tasks} totalQuestions={state.total_questions} />
                </ChartCard>
                <ChartCard>
                  <SuccessFailurePie metrics={state.metrics} tasks={state.tasks} totalQuestions={state.total_questions} type="sql" />
                </ChartCard>
              </div>

              <Card className="flex h-[800px] flex-col">
                <CardHeader
                  title="Vragen"
                  description="Klik op een vraag voor de volledige trace."
                  actions={
                    <div className="hidden items-center gap-3 text-xs sm:flex">
                      {LEGEND.map((l) => (
                        <span key={l.label} className="flex items-center gap-1.5 text-muted-foreground">
                          <span className={`h-2 w-2 rounded-full ${l.cls}`} />
                          {l.label}
                        </span>
                      ))}
                    </div>
                  }
                />
                <div className="min-h-0 flex-1 overflow-hidden p-4">
                  <TaskList tasks={state.tasks} onTaskClick={state.openTraceViewer} totalQuestions={state.total_questions} />
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
